import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Slide } from '@core/models-v2/cms/slider-reponse.interface';

interface MetaTagConfig {
  title: string;
  description: string;
  image: string;
  url: string;
}

@Injectable({ providedIn: 'root' })
export class HeroCarouselMetaService {
  private readonly platformId = inject(PLATFORM_ID);

  updateCarouselMeta(slide: Slide, imageUrl: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const config: MetaTagConfig = {
      title: `${slide.alt} - Implementos`,
      description: slide.title || 'Repuestos para Camiones, Buses y Remolque',
      image: imageUrl,
      url: slide.url,
    };

    this.updateMetaTags(config);
    this.addStructuredData(slide, imageUrl);
  }

  private updateMetaTags(config: MetaTagConfig): void {
    const metaTags = [
      { property: 'og:title', content: config.title },
      { property: 'og:description', content: config.description },
      { property: 'og:image', content: config.image },
      { property: 'og:url', content: config.url },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: config.title },
      { name: 'twitter:description', content: config.description },
      { name: 'twitter:image', content: config.image },
      { name: 'description', content: config.description },
    ];

    metaTags.forEach((tag) => this.updateOrCreateMetaTag(tag));
  }

  private updateOrCreateMetaTag(tag: {
    property?: string;
    name?: string;
    content: string;
  }): void {
    const selector = tag.property
      ? `meta[property="${tag.property}"]`
      : `meta[name="${tag.name}"]`;

    let meta = document.querySelector(selector) as HTMLMetaElement;

    if (!meta) {
      meta = document.createElement('meta');
      if (tag.property) meta.setAttribute('property', tag.property);
      if (tag.name) meta.setAttribute('name', tag.name);
      document.head.appendChild(meta);
    }

    meta.setAttribute('content', tag.content);
  }

  private addStructuredData(slide: Slide, imageUrl: string): void {
    const existingScript = document.querySelector(
      'script[type="application/ld+json"][data-carousel]'
    );
    if (existingScript) return;

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: slide.alt,
      description: slide.title || slide.alt,
      image: imageUrl,
      url: slide.url,
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceCurrency: 'CLP',
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-carousel', 'true');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }
}
